import { Flex } from 'styling/GlobalStyles'
import { GLDetailsCardContainer, TripCard } from '../GlobalView.styled'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { darkenHex, formatSecondsToTime } from 'utils/helpers'
import { useEffect, useRef, useState } from 'react'
import { IMachineTrip } from 'types/api/globalViewApi.interface'
import { TripPinIcon } from 'assets/icons/TripPinIcon'
import { TripClockIcon } from 'assets/icons/TripClockIcon'

export const MachineTrips = ({ map, closeTrips }: { map: google.maps.Map, closeTrips: () => void }) => {

    const { machineInfo } = useSelector((state: RootState) => state.globalView)
    const polylinesRef = useRef<google.maps.Polyline[]>([])

    // Changing from hover index to selected ID for the "click to focus" requirement
    const [selectedTripId, setSelectedTripId] = useState<string | null>(null)
    const animationFrameRef = useRef<number | null>(null)

    const tripColors = useRef<string[]>([
        '#4dabf7', '#ff6b6b', '#ffa94d', '#51cf66', '#fcc419',
        '#22b8cf', '#845ef7', '#94d82d', '#20c997', '#f06595',
    ])

    // 1. Initialize Polylines
    useEffect(() => {
        // Cleanup existing lines
        polylinesRef.current.forEach(p => p.setMap(null))
        polylinesRef.current = []
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)

        if (!map || !machineInfo?.trips?.length) return


        const arrowSymbol = {
            path:
                'M1.43009 1.00181C1.52669 0.994466 1.62395 1.00956 1.70894 1.0451L1.71016 1.04561L6.75348 3.18354C6.82789 3.21481 6.89055 3.26082 6.93378 3.31632C6.97714 3.37197 7 3.4354 7 3.5C7 3.5646 6.97714 3.62803 6.93378 3.68368C6.89055 3.73918 6.82853 3.78492 6.75412 3.81619L1.70894 5.95491C1.62396 5.99044 1.52669 6.00553 1.43009 5.99819C1.33349 5.99084 1.24213 5.9614 1.16819 5.91378C1.09425 5.86616 1.04122 5.80263 1.01618 5.73163C0.991159 5.66072 0.995238 5.58573 1.02787 5.51663L1.57624 4.35243C1.83043 3.81277 1.83047 3.18782 1.57634 2.64814L1.02799 1.48362C0.99531 1.41449 0.991146 1.33931 1.01618 1.26837C1.04122 1.19737 1.09425 1.13384 1.16819 1.08622C1.24213 1.0386 1.33349 1.00916 1.43009 1.00181ZM6.63781 3.34177L6.52105 3.49954L1.47786 1.36167L6.52214 3.5L2.23443 4.92992C2.27568 5.0101 2.46761 3.54449 2.42635 3.62467L2.1842 4.94667L6.52105 3.49954L6.52214 3.5L6.63781 3.34177Z ' +
                'M2.5598 3.09453C2.83096 3.14675 3.02981 3.298 3.00393 3.43235L2.62912 5.37844C2.60325 5.51278 2.36245 5.57936 2.0913 5.52714C1.82014 5.47491 1.62129 5.32367 1.64717 5.18932L2.02198 3.24323C2.04785 3.10888 2.28865 3.0423 2.5598 3.09453Z',

            fillColor: '#ffffff',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 0,
            scale: 3,

            anchor: new google.maps.Point(4, 3.5),
            rotation: -90,
        };



        const newPolylines = machineInfo.trips.map((t: IMachineTrip, i: number) => {
            const locs = (t.locations as unknown as { latitude: number; longitude: number }[])
            const path = (locs || [])
                .filter((l) => typeof l.latitude === 'number' && typeof l.longitude === 'number')
                .map((l) => ({ lat: Number(l.latitude), lng: Number(l.longitude) }))

            const color = tripColors.current[i % tripColors.current.length];

            const polyline = new google.maps.Polyline({
                map,
                path,
                strokeColor: color,
                strokeOpacity: 1.0, // Solid line for better contrast with the arrow
                strokeWeight: 6, // Thicker line to contain the arrow nicely
                icons: [{
                    icon: {
                        path:
                            'M1.43009 1.00181C1.52669 0.994466 1.62395 1.00956 1.70894 1.0451L1.71016 1.04561L6.75348 3.18354C6.82789 3.21481 6.89055 3.26082 6.93378 3.31632C6.97714 3.37197 7 3.4354 7 3.5C7 3.5646 6.97714 3.62803 6.93378 3.68368C6.89055 3.73918 6.82853 3.78492 6.75412 3.81619L1.70894 5.95491C1.62396 5.99044 1.52669 6.00553 1.43009 5.99819C1.33349 5.99084 1.24213 5.9614 1.16819 5.91378C1.09425 5.86616 1.04122 5.80263 1.01618 5.73163C0.991159 5.66072 0.995238 5.58573 1.02787 5.51663L1.57624 4.35243C1.83043 3.81277 1.83047 3.18782 1.57634 2.64814L1.02799 1.48362C0.99531 1.41449 0.991146 1.33931 1.01618 1.26837C1.04122 1.19737 1.09425 1.13384 1.16819 1.08622C1.24213 1.0386 1.33349 1.00916 1.43009 1.00181ZM6.63781 3.34177L6.52105 3.49954L1.47786 1.36167L6.52214 3.5L2.23443 4.92992C2.27568 5.0101 2.46761 3.54449 2.42635 3.62467L2.1842 4.94667L6.52105 3.49954L6.52214 3.5L6.63781 3.34177Z ' +
                            'M2.5598 3.09453C2.83096 3.14675 3.02981 3.298 3.00393 3.43235L2.62912 5.37844C2.60325 5.51278 2.36245 5.57936 2.0913 5.52714C1.82014 5.47491 1.62129 5.32367 1.64717 5.18932L2.02198 3.24323C2.04785 3.10888 2.28865 3.0423 2.5598 3.09453Z',

                        fillColor: darkenHex(color),
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 0,
                        scale: 3,

                        anchor: new google.maps.Point(4, 3.5),
                        rotation: -90,
                    },
                    offset: '0%',
                    repeat: '50px' // Repeat every 50 pixels (screen pixels)
                }],
                zIndex: 1
            })

            // Store the ID on the object for easy filtering later
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            polyline._tripId = t.id

            return polyline
        })

        polylinesRef.current = newPolylines

        // Fit bounds to all trips initially
        try {
            const bounds = new google.maps.LatLngBounds()
            newPolylines.forEach(pl => {
                pl.getPath().forEach(latLng => bounds.extend(latLng))
            })
            if (!bounds.isEmpty()) {
                map.fitBounds(bounds)
            }
        } catch { }

        // Start the animation loop
        animateArrows()

        return () => {
            polylinesRef.current.forEach(p => p.setMap(null))
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
        }
    }, [map, machineInfo?.trips])

    // 2. Animation Logic (Moving Arrows)
    const animateArrows = () => {
        const speed = 15; // Lower is faster
        const time = performance.now();

        // We calculate offset based on time to ensure constant speed regardless of zoom or framerate
        // The modulo 50 ensures it loops perfectly with the repeat: '50px' set above
        const offset = (time / speed) % 50;

        polylinesRef.current.forEach((polyline) => {
            // Only animate if the line is visible on the map
            if (polyline.getMap()) {
                const icons = polyline.get('icons');
                if (icons && icons[0]) {
                    icons[0].offset = `${offset}px`; // Pixel offset ensures constant visual speed
                    polyline.set('icons', icons);
                }
            }
        });

        animationFrameRef.current = requestAnimationFrame(animateArrows);
    };

    // 3. Handle Selection (Click Logic)
    useEffect(() => {
        if (!polylinesRef.current.length) return

        const bounds = new google.maps.LatLngBounds()
        let hasVisible = false

        polylinesRef.current.forEach((pl) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const isMatch = pl._tripId === selectedTripId

            // If nothing is selected, show all. If something is selected, show only that one.
            const shouldShow = selectedTripId === null || isMatch

            pl.setMap(shouldShow ? map : null)

            // Visual tweaks for the selected line
            pl.setOptions({
                zIndex: isMatch ? 100 : 1, // Bring selected to front
                strokeOpacity: selectedTripId && !isMatch ? 0 : 1 // Redundant with setMap but good for transitions if needed
            })

            if (shouldShow) {
                pl.getPath().forEach(latLng => bounds.extend(latLng))
                hasVisible = true
            }
        })

        // Optional: Re-focus map on the specific trip when clicked
        if (selectedTripId && hasVisible && map) {
            map.fitBounds(bounds)
        }

    }, [selectedTripId, map])

    const handleCardClick = (id: string) => {
        // Toggle: If clicking the already selected one, clear selection. Otherwise select it.
        setSelectedTripId(prev => prev === id ? null : id)
    }

    return <GLDetailsCardContainer style={{ marginTop: '3rem' }} >
        <Flex width='100%' between >
            <Flex column gap='1rem' >
                <TitleText weight='bold' >Trips Today</TitleText>
                <OtherText onClick={closeTrips} style={{ color: '#ff9900', cursor: 'pointer', width: 'max-content' }}> Close Trips Preview</OtherText>
            </Flex>
            <TitleText weight='bold' >{machineInfo?.trips?.length}</TitleText>
        </Flex>
        {machineInfo?.trips.map((t, i) => {
            const color = tripColors.current[i % tripColors.current.length]
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const isSelected = selectedTripId === t.id

            // We fade out the cards that aren't selected if one IS selected
            const opacity = selectedTripId && !isSelected ? 0.5 : 1

            return (
                <TripCard
                    key={t.id}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    onClick={() => handleCardClick(t.id)}
                    style={{
                        borderLeft: `4px solid ${color}`,
                        cursor: 'pointer',
                        opacity,
                        transition: 'opacity 0.2s ease-in-out',
                    }}
                >
                    <div className='top' >
                        <Flex gap='0.5rem' center >
                            <TripPinIcon color={color} />
                            <TitleText weight='bold' > Trip {i + 1} </TitleText>
                        </Flex>
                        <Flex gap='0.5rem' center >
                            <TripClockIcon />
                            <BodyText > {formatTimeRange(new Date(t.startDate), t.duration)} </BodyText>
                        </Flex>
                    </div>
                    <div className='bottom' >
                        <Flex gap='2rem' >
                            <OtherText>Distance</OtherText>
                            <OtherText>{t.distance || '/'} km</OtherText>
                        </Flex>
                        <Flex gap='2rem' >
                            <OtherText>Duration</OtherText>
                            <OtherText>{formatSecondsToTime(t.duration, true)}</OtherText>
                        </Flex>
                    </div>
                </TripCard>
            )
        })}

    </GLDetailsCardContainer>
}

function formatTimeRange(startTime: Date, durationSeconds: number): string {
    function formatTime(date: Date): string {
        const hrs24 = date.getHours();
        const mins = date.getMinutes();

        const period = hrs24 >= 12 ? 'pm' : 'am';
        const hrs12 = hrs24 % 12 === 0 ? 12 : hrs24 % 12;
        const paddedMins = mins.toString().padStart(2, '0');

        return `${hrs12}:${paddedMins} ${period}`;
    }

    const endTime = new Date(startTime.getTime() + durationSeconds * 1000);

    const formattedStart = formatTime(startTime);
    const formattedEnd = formatTime(endTime);

    return `${formattedStart} > ${formattedEnd}`;
}